/**
 * Asset Upload Service
 *
 * Single source of truth for all upload pre-processing logic.
 * Used by AdminAssetUpload.vue, useClientUploadWizard.js, and clientAssetsStore.js.
 */
import { collection, query, where, getDocs, limit, doc, deleteDoc } from 'firebase/firestore';
import { db } from '@/js/firebase';
import { logger } from './loggerService';
import { memoryProbe } from './memoryProbe';
import { getOrientation } from '@/utils/assetUtils';

/**
 * Delete an asset document by id. Keeps Firestore writes out of components
 * (CLAUDE.md: "Never edit Firestore directly from components — use stores and
 * services"). Bunny artifact cleanup is handled separately: the
 * onAssetDeletedBunnyCleanup Firestore trigger fires on this deletion, so the
 * media is never orphaned even without an explicit pre-delete callable.
 *
 * @param {string} assetId
 * @returns {Promise<void>}
 */
export async function deleteAssetDoc(assetId) {
  if (!assetId) return;
  await deleteDoc(doc(db, 'assets', assetId));
}

/**
 * Detect asset type from a File object using MIME type, extension, and filename patterns.
 * Returns valid ASSET_TYPES keys (videos, selects, photography, audio, documents, brand assets, etc.)
 *
 * @param {File} file - Browser File object
 * @returns {string} Asset type key
 */
export function detectAssetType(file) {
  try {
    const type = file?.type?.toLowerCase() || '';
    const name = file?.name?.toLowerCase() || '';
    const extension = name.split('.').pop()?.toLowerCase() || '';

    // Image files
    if (
      type.startsWith('image/') ||
      // heic/heif: iOS photos often arrive with an empty file.type, so the
      // extension is the only signal — without it they fall through to 'brand assets'
      ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif', 'heic', 'heif'].includes(extension)
    ) {
      return 'photography';
    }

    // Audio files
    if (
      type.startsWith('audio/') ||
      ['mp3', 'wav', 'aac', 'ogg', 'flac', 'm4a', 'wma'].includes(extension)
    ) {
      return 'audio';
    }

    // Video files - detect by name patterns first, then default to 'videos'
    if (
      type.startsWith('video/') ||
      ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm', 'm4v', '3gp'].includes(extension)
    ) {
      // Smart detection based on filename patterns
      if (
        name.includes('select') ||
        name.includes('final') ||
        name.includes('cut') ||
        name.includes('edit')
      ) {
        return 'selects';
      }

      // Check for common short-form video indicators
      if (
        name.includes('short') ||
        name.includes('clip') ||
        name.includes('snippet') ||
        name.includes('teaser')
      ) {
        return 'selects';
      }

      // Default to videos for all other video files
      return 'videos';
    }

    // Brand/design files
    if (
      ['pdf', 'ai', 'psd', 'eps', 'indd', 'sketch', 'fig', 'xd'].includes(extension)
    ) {
      return 'brand assets';
    }

    // Document files
    if (['doc', 'docx', 'txt', 'rtf', 'odt'].includes(extension)) {
      return 'brand assets';
    }

    // Spreadsheet files
    if (['xls', 'xlsx', 'csv', 'ods'].includes(extension)) {
      return 'brand assets';
    }

    // Presentation files
    if (['ppt', 'pptx', 'odp', 'key'].includes(extension)) {
      return 'brand assets';
    }

    // Archive files
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
      return 'brand assets';
    }

    return 'brand assets'; // Default fallback
  } catch (_error) {
    logger.error('[AssetUploadService] Error detecting asset type:', _error);
    return 'videos'; // Safe fallback
  }
}

/**
 * Classify a video by its duration.
 * Returns 'selects' for <=20s, 'videos' for >20s.
 *
 * @param {number} duration - Video duration in seconds
 * @param {string} [currentType] - Current detected type (unused, kept for future use)
 * @returns {string} 'selects' or 'videos'
 */
export function classifyVideoByDuration(duration) {
  if (duration && duration > 0 && duration <= 20) {
    return 'selects';
  }
  return 'videos';
}

/**
 * Sanitize a filename for Firebase Storage.
 *
 * @param {string} name - Original filename
 * @returns {string} Sanitized filename
 */
function sanitizeFileName(name) {
  return name.replace(/[^a-zA-Z0-9-_.]/g, '_');
}

function stripFileExtension(name, fallback = 'file') {
  return String(name || fallback).replace(/\.[^./]+$/, '');
}

/**
 * Canonical asset name (basename, no extension) as stored in the `name`
 * field on every asset doc. This MUST be the single transform used by both
 * the upload write path and the duplicate-name check, or the two diverge
 * (e.g. "My Photo.jpg" stored as "My_Photo" but queried as "My Photo").
 *
 * @param {string} fileName - Original filename including extension
 * @returns {string} Sanitized basename without extension
 */
export function canonicalAssetName(fileName) {
  return stripFileExtension(sanitizeFileName(fileName || ''));
}

/**
 * Build Firebase Storage upload metadata for a file.
 * Sets contentType and contentDisposition to force download behavior.
 *
 * @param {File|Blob} file - Browser File/Blob object
 * @param {string} [fileName] - Override filename (defaults to file.name)
 * @returns {Object} Metadata object for uploadBytesResumable/uploadBytes
 */
export function buildUploadMetadata(file, fileName) {
  const name = fileName || file.name || 'download';
  const encoded = encodeURIComponent(name);
  return {
    contentType: file.type || 'application/octet-stream',
    contentDisposition: `attachment; filename="${encoded}"`,
    customMetadata: { originalName: encoded }
  };
}

/**
 * Check if an asset with the same name already exists in the company
 * library. Storage-backend-agnostic — works for both Firebase- and
 * Bunny-backed assets because it queries the Firestore asset doc, not
 * the underlying storage object.
 *
 * `name` on every asset doc is stored as canonicalAssetName(file.name)
 * (sanitized, then extension stripped), so we apply the SAME transform to
 * the input before comparing — otherwise filenames with spaces/special
 * chars (stored as "My_Photo" but queried as "My Photo") never match.
 *
 * @param {string} fileName - Original filename including extension
 * @param {string} companyId - Firestore companies/{id}
 * @returns {Promise<boolean>} True if a same-name asset already exists
 */
export async function checkFileExistsInStorage(fileName, companyId) {
  if (!companyId || !fileName) return false;
  try {
    const canonicalName = canonicalAssetName(fileName);
    const q = query(
      collection(db, 'assets'),
      where('company', '==', companyId),
      where('name', '==', canonicalName),
      limit(1),
    );
    const snap = await getDocs(q);
    return !snap.empty;
  } catch (error) {
    logger.warn('[AssetUploadService] Error checking asset existence', {
      fileName,
      companyId,
      error: error.message,
    });
    return false; // Fail-open so a transient Firestore error doesn't block the upload
  }
}

/**
 * Get the duration of a video file using a DOM video element.
 *
 * @param {File} file - Browser File object (video)
 * @returns {Promise<number|null>} Duration in seconds, or null if unavailable
 */
function getVideoDuration(file) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);

    const timeout = setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
      logger.warn('[AssetUploadService] Video duration calculation timed out');
      resolve(null);
    }, 10000);

    video.onloadedmetadata = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(objectUrl);
      if (video.duration && !isNaN(video.duration) && isFinite(video.duration) && video.duration > 0) {
        resolve(video.duration);
      } else {
        resolve(null);
      }
    };
    video.onerror = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(objectUrl);
      logger.warn('[AssetUploadService] Failed to load video for duration calculation (upload will proceed without duration)');
      resolve(null);
    };
    // Only fetch metadata, not the full file — avoids buffering large videos into memory
    video.preload = 'metadata';
    video.src = objectUrl;
  });
}

/**
 * Get the duration of an audio file using a DOM audio element.
 *
 * @param {File} file - Browser File object (audio)
 * @returns {Promise<number|null>} Duration in seconds, or null if unavailable
 */
function getAudioDuration(file) {
  return new Promise((resolve) => {
    const audio = document.createElement('audio');
    const objectUrl = URL.createObjectURL(file);

    const timeout = setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
      logger.warn('[AssetUploadService] Audio duration calculation timed out');
      resolve(null);
    }, 10000);

    audio.onloadedmetadata = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(objectUrl);
      if (audio.duration && isFinite(audio.duration)) {
        resolve(audio.duration);
      } else {
        logger.warn('[AssetUploadService] Audio duration is invalid or infinite');
        resolve(null);
      }
    };

    audio.onerror = (_error) => {
      clearTimeout(timeout);
      URL.revokeObjectURL(objectUrl);
      logger.error('[AssetUploadService] Failed to load audio for duration calculation:', _error);
      resolve(null);
    };

    // Only fetch metadata, not the full file — avoids buffering large audio into memory
    audio.preload = 'metadata';
    audio.src = objectUrl;
  });
}

/**
 * Get orientation and dimensions of an image file.
 *
 * @param {File} file - Browser File object (image)
 * @returns {Promise<{orientation: string|null, width: number|null, height: number|null}>}
 */
function getImageMediaInfo(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        orientation: getOrientation(img.width, img.height),
        width: img.width,
        height: img.height
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      logger.warn('[AssetUploadService] Failed to load image for media info');
      resolve({ orientation: 'square', width: null, height: null });
    };
    img.src = objectUrl;
  });
}

/**
 * Get orientation and dimensions of a video file.
 *
 * @param {File} file - Browser File object (video)
 * @returns {Promise<{orientation: string|null, width: number|null, height: number|null}>}
 */
function getVideoMediaInfo(file) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);

    const timeout = setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
      logger.warn('[AssetUploadService] Video media info calculation timed out');
      // Unknown, not horizontal. Guessing an orientation here files undecodable
      // videos under the wrong library filter; onBunnyStreamReady fills these in
      // from Bunny's own encode once transcoding finishes.
      resolve({ orientation: null, width: null, height: null });
    }, 10000);

    video.onloadedmetadata = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(objectUrl);
      resolve({
        orientation: getOrientation(video.videoWidth, video.videoHeight),
        width: video.videoWidth,
        height: video.videoHeight
      });
    };
    video.onerror = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(objectUrl);
      logger.warn('[AssetUploadService] Failed to load video for media info (upload will proceed without dimensions)');
      resolve({ orientation: null, width: null, height: null });
    };
    // Only fetch metadata, not the full file — avoids buffering large videos into memory
    video.preload = 'metadata';
    video.src = objectUrl;
  });
}

/**
 * Get media metadata (orientation, dimensions, duration) for a file.
 * Dispatches to the appropriate function based on MIME type.
 *
 * @param {File} file - Browser File object
 * @returns {Promise<{aspectRatio: string|null, width: number|null, height: number|null, duration: number|null}>}
 */
export async function getMediaMetadata(file) {
  const result = { aspectRatio: null, width: null, height: null, duration: null };

  if (!file?.type) return result;

  // Each probe spins up a transient <video>/<audio> decoder. Track it so we can
  // correlate upload concurrency with memory pressure (see memoryProbe.js).
  memoryProbe.decoderOpened();
  memoryProbe.log('probe start', { name: file.name, type: file.type, sizeMB: Math.round((file.size || 0) / (1024 * 1024)) });
  try {
    if (file.type.startsWith('image/')) {
      const info = await getImageMediaInfo(file);
      result.aspectRatio = info.orientation;
      result.width = info.width;
      result.height = info.height;
    } else if (file.type.startsWith('video/')) {
      const info = await getVideoMediaInfo(file);
      result.aspectRatio = info.orientation;
      result.width = info.width;
      result.height = info.height;
      result.duration = await getVideoDuration(file);
    } else if (file.type.startsWith('audio/')) {
      result.duration = await getAudioDuration(file);
    }
  } finally {
    memoryProbe.decoderClosed();
    memoryProbe.log('probe end', { name: file.name });
  }

  return result;
}

/**
 * Check if a file type is blocked from upload.
 *
 * MOV files were previously blocked due to in-browser playback compatibility
 * issues. With Bunny Stream transcoding uploads server-side, the source
 * container no longer affects playback, so MOV is now allowed. This guard is
 * retained as the single extension point should we need to block a type again.
 *
 * @param {File} file - Browser File object
 * @returns {{blocked: boolean, reason: string|null}}
 */
// eslint-disable-next-line no-unused-vars
export function isFileTypeBlocked(file) {
  return { blocked: false, reason: null };
}
